/**
 * Bausteine der Oberfläche.
 *
 * Bewusst eine einzige Datei: Es sind kleine, eng verwandte Bausteine, die in
 * jeder Ansicht gemeinsam auftreten. Alle Zustände (hell/dunkel, Schweregrade,
 * Größen) sind hier zentral definiert – Ansichten setzen keine Farben selbst,
 * damit das Panel überall gleich aussieht.
 */
import clsx from 'clsx';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

// ---------------------------------------------------------------------------
// Flächen
// ---------------------------------------------------------------------------

export function Card({
  title,
  subtitle,
  actions,
  children,
  className,
  padded = true,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={clsx('surface flex flex-col', className)}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b divider px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="truncate text-sm font-semibold">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-panel-500 dark:text-panel-400">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={clsx('min-w-0 flex-1', padded && 'p-4')}>{children}</div>
    </section>
  );
}

/** Kennzahlenkachel für Dashboards. */
export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'positive' | 'negative' | 'warning';
  icon?: ReactNode;
}) {
  const toneClass = {
    neutral: 'text-panel-900 dark:text-panel-50',
    positive: 'text-accent-600 dark:text-accent-400',
    negative: 'text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400',
  }[tone];

  return (
    <div className="surface p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="label">{label}</span>
        {icon && <span className="text-panel-400 dark:text-panel-500">{icon}</span>}
      </div>
      <div className={clsx('mt-1.5 truncate text-xl font-semibold tabular', toneClass)}>{value}</div>
      {hint && <div className="mt-1 truncate text-xs text-panel-500 dark:text-panel-400">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schaltflächen und Eingaben
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  size = 'md',
  loading,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'md'; loading?: boolean }) {
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  const sizes = { sm: 'px-2.5 py-1 text-xs', md: 'px-3 py-1.5 text-sm' }[size];
  const variants = {
    primary: 'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800',
    secondary:
      'border border-panel-300 bg-white text-panel-800 hover:bg-panel-100 dark:border-panel-700 dark:bg-panel-800 dark:text-panel-100 dark:hover:bg-panel-700',
    ghost: 'text-panel-600 hover:bg-panel-200/70 dark:text-panel-300 dark:hover:bg-panel-800',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  }[variant];

  return (
    <button className={clsx(base, sizes, variants, className)} disabled={loading || props.disabled} {...props}>
      {loading && <Spinner className="h-3 w-3" />}
      {children}
    </button>
  );
}

export function Input({ label, hint, className, ...props }: InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string }) {
  return (
    <label className="block">
      {label && <span className="label mb-1 block">{label}</span>}
      <input
        className={clsx(
          'w-full rounded-md border border-panel-300 bg-white px-2.5 py-1.5 text-sm placeholder:text-panel-400',
          'focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500',
          'dark:border-panel-700 dark:bg-panel-950 dark:placeholder:text-panel-600',
          className,
        )}
        {...props}
      />
      {hint && <span className="mt-1 block text-xs text-panel-500 dark:text-panel-400">{hint}</span>}
    </label>
  );
}

export function Select({
  label,
  hint,
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: string }) {
  return (
    <label className="block">
      {label && <span className="label mb-1 block">{label}</span>}
      <select
        className={clsx(
          'w-full rounded-md border border-panel-300 bg-white px-2.5 py-1.5 text-sm',
          'focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500',
          'dark:border-panel-700 dark:bg-panel-950',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {hint && <span className="mt-1 block text-xs text-panel-500 dark:text-panel-400">{hint}</span>}
    </label>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={clsx('animate-spin', className ?? 'h-4 w-4')} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Statusanzeigen
// ---------------------------------------------------------------------------

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-panel-200 text-panel-800 dark:bg-panel-700 dark:text-panel-100',
  success: 'bg-accent-100 text-accent-800 dark:bg-accent-950 dark:text-accent-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  danger: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300',
  info: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300',
  muted: 'bg-panel-100 text-panel-500 dark:bg-panel-800/60 dark:text-panel-400',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={clsx('inline-flex items-center rounded px-1.5 py-0.5 text-2xs font-medium', TONE_CLASSES[tone], className)}>
      {children}
    </span>
  );
}

/** Statuspunkt mit Beschriftung – für Geräte- und Sessionzustände. */
export function StatusDot({ tone, label, pulse }: { tone: Tone; label?: ReactNode; pulse?: boolean }) {
  const dot = {
    neutral: 'bg-panel-400',
    success: 'bg-accent-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    info: 'bg-sky-500',
    muted: 'bg-panel-300 dark:bg-panel-600',
  }[tone];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={clsx('h-2 w-2 shrink-0 rounded-full', dot, pulse && 'animate-pulse-soft')} />
      {label && <span className="truncate text-xs">{label}</span>}
    </span>
  );
}

/** Auslastungsbalken; färbt sich ab 75 % bzw. 92 % um. */
export function Meter({ value, max, label, invert }: { value: number; max: number; label?: ReactNode; invert?: boolean }) {
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const tone = invert
    ? ratio < 0.25
      ? 'bg-red-500'
      : ratio < 0.6
        ? 'bg-amber-500'
        : 'bg-accent-500'
    : ratio >= 0.92
      ? 'bg-red-500'
      : ratio >= 0.75
        ? 'bg-amber-500'
        : 'bg-accent-500';

  return (
    <div className="w-full">
      {label && <div className="mb-1 flex justify-between text-2xs text-panel-500 dark:text-panel-400">{label}</div>}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-panel-200 dark:bg-panel-800">
        <div className={clsx('h-full rounded-full transition-all duration-500', tone)} style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabelle
// ---------------------------------------------------------------------------

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Zellinhalt. */
  render: (row: T) => ReactNode;
  /** Wert für die Sortierung; fehlt er, ist die Spalte nicht sortierbar. */
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
  width?: string;
  /** Auf schmalen Bildschirmen ausblenden. */
  hideOnMobile?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty = 'Keine Einträge vorhanden.',
  dense,
  maxHeight,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  dense?: boolean;
  maxHeight?: string;
}) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;
    return [...rows].sort((a, b) => {
      const av = column.sortValue!(a);
      const bv = column.sortValue!(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv), 'de') * sort.dir;
    });
  }, [rows, sort, columns]);

  const cell = dense ? 'px-2.5 py-1.5' : 'px-3 py-2';

  return (
    <div className={clsx('overflow-auto', maxHeight)} style={maxHeight ? undefined : undefined}>
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-panel-100 text-left dark:bg-panel-950">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
                className={clsx(
                  cell,
                  'border-b divider text-2xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400',
                  column.align === 'right' && 'text-right',
                  column.align === 'center' && 'text-center',
                  column.hideOnMobile && 'hidden md:table-cell',
                  column.sortValue && 'cursor-pointer select-none hover:text-panel-800 dark:hover:text-panel-200',
                )}
                onClick={
                  column.sortValue
                    ? () =>
                        setSort((current) =>
                          current?.key === column.key ? { key: column.key, dir: current.dir === 1 ? -1 : 1 } : { key: column.key, dir: 1 },
                        )
                    : undefined
                }
              >
                <span className="inline-flex items-center gap-1">
                  {column.header}
                  {sort?.key === column.key && <span aria-hidden>{sort.dir === 1 ? '▲' : '▼'}</span>}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center text-sm text-panel-500 dark:text-panel-400">
                {empty}
              </td>
            </tr>
          )}
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={clsx(
                'border-b divider last:border-0',
                onRowClick && 'cursor-pointer hover:bg-panel-100 dark:hover:bg-panel-800/60',
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={clsx(
                    cell,
                    'align-middle',
                    column.align === 'right' && 'text-right tabular',
                    column.align === 'center' && 'text-center',
                    column.hideOnMobile && 'hidden md:table-cell',
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reiter
// ---------------------------------------------------------------------------

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: ReactNode; badge?: ReactNode }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b divider" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={active === tab.id}
          onClick={() => onChange(tab.id)}
          className={clsx(
            '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
            active === tab.id
              ? 'border-accent-500 text-accent-700 dark:text-accent-400'
              : 'border-transparent text-panel-500 hover:text-panel-800 dark:text-panel-400 dark:hover:text-panel-200',
          )}
        >
          <span className="inline-flex items-center gap-1.5">
            {tab.label}
            {tab.badge}
          </span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  // Schließen mit Escape gehört zur Grundbedienbarkeit.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-panel-950/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        className={clsx('surface relative z-10 max-h-[85vh] w-full animate-fade-in overflow-hidden', wide ? 'max-w-3xl' : 'max-w-lg')}
      >
        <header className="flex items-center justify-between border-b divider px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Schließen">
            ✕
          </Button>
        </header>
        <div className="max-h-[60vh] overflow-auto p-4">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t divider px-4 py-3">{footer}</footer>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hinweise
// ---------------------------------------------------------------------------

export function Notice({ tone = 'info', title, children }: { tone?: Tone; title?: ReactNode; children: ReactNode }) {
  const border = {
    neutral: 'border-l-panel-400',
    success: 'border-l-accent-500',
    warning: 'border-l-amber-500',
    danger: 'border-l-red-500',
    info: 'border-l-sky-500',
    muted: 'border-l-panel-300',
  }[tone];
  return (
    <div className={clsx('surface-muted border-l-4 p-3 text-sm', border)}>
      {title && <div className="mb-0.5 font-medium">{title}</div>}
      <div className="text-panel-600 dark:text-panel-300">{children}</div>
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="max-w-md text-xs text-panel-500 dark:text-panel-400">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Beschriftetes Wertepaar für Detailansichten. */
export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="label">{label}</div>
      <div className="mt-0.5 truncate text-sm">{children}</div>
    </div>
  );
}

/** Monospace-Darstellung technischer Werte (IPs, Präfixe, AS-Pfade). */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <code className={clsx('font-mono text-xs', className)}>{children}</code>;
}

// ---------------------------------------------------------------------------
// Kontext für Seitenüberschriften
// ---------------------------------------------------------------------------

interface PageHeaderContext {
  setActions: (actions: ReactNode) => void;
}

const HeaderContext = createContext<PageHeaderContext | null>(null);
export const HeaderProvider = HeaderContext.Provider;
export const usePageHeader = () => useContext(HeaderContext);

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-panel-500 dark:text-panel-400">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Rasterlayout für Kennzahlenkacheln. */
export function StatGrid({ children, cols = 4 }: { children: ReactNode; cols?: 2 | 3 | 4 | 5 }) {
  const map = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4', 5: 'sm:grid-cols-2 lg:grid-cols-5' };
  return <div className={clsx('grid grid-cols-1 gap-3', map[cols])}>{children}</div>;
}
